import { useEffect, useState, type ReactNode } from "react";
import { motion } from "motion/react";
import { CoolIcon } from "@/components/ui/cool-icon";
import {
  getTodayWindow,
  type TradingWindow,
  type WindowStatus,
} from "@/lib/api/window";
import { cn } from "@/lib/utils";

/**
 * Top-of-overview strip that surfaces the single most operator-critical
 * signal: is the bot trading right now, and for how much longer?
 *
 * Driven by /api/window/today (stable per UTC day) + client-side countdown
 * that ticks every second. Re-fetches every 5 min so a day-rollover at UTC
 * midnight flips state without a page reload.
 */

interface StatusTone {
  tone: "live" | "warn" | "idle";
  label: string;
  glyph: string;
  countdownTo: "start" | "forceClose" | "windowEnd" | null;
}

const TONE_CLASSES: Record<StatusTone["tone"], string> = {
  live: "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  warn: "border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400",
  idle: "border-muted-foreground/20 bg-muted/40 text-muted-foreground",
};

function describe(w: TradingWindow | null): StatusTone {
  if (!w) {
    return { tone: "idle", label: "Loading…", glyph: "Loading", countdownTo: null };
  }
  switch (w.status) {
    case "weekend":
      return {
        tone: "idle",
        label: "Weekend — Bots Offline",
        glyph: "Moon",
        countdownTo: null,
      };
    case "pre_open":
      return {
        tone: "warn",
        label: "Opening In",
        glyph: "Sun",
        countdownTo: "start",
      };
    case "open":
      return {
        tone: "live",
        label: "Live — Force-Close In",
        glyph: "Chart_Line",
        countdownTo: "forceClose",
      };
    case "closing":
      return {
        tone: "warn",
        label: "Closing — Window Ends In",
        glyph: "Timer",
        countdownTo: "windowEnd",
      };
    case "post_close":
      return {
        tone: "idle",
        label: "Closed — Next Window Tomorrow",
        glyph: "Clock",
        countdownTo: null,
      };
  }
}

function formatDuration(ms: number): string {
  if (ms <= 0) return "0s";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s.toString().padStart(2, "0")}s`;
  return `${s}s`;
}

function targetTsFor(w: TradingWindow, t: StatusTone["countdownTo"]): number | null {
  if (t === "start") return w.windowStart;
  if (t === "forceClose") return w.forceCloseAt;
  if (t === "windowEnd") return w.windowEnd;
  return null;
}

function formatClock(ts: number): string {
  const d = new Date(ts);
  const hh = d.getUTCHours().toString().padStart(2, "0");
  const mm = d.getUTCMinutes().toString().padStart(2, "0");
  return `${hh}:${mm} UTC`;
}

export function HeroStatusStrip(): ReactNode {
  const [win, setWin] = useState<TradingWindow | null>(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = () =>
      getTodayWindow()
        .then((w) => alive && setWin(w))
        .catch(() => undefined);
    load();
    const poll = setInterval(load, 5 * 60_000);
    return () => {
      alive = false;
      clearInterval(poll);
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const status = describe(win);
  const target = win && status.countdownTo ? targetTsFor(win, status.countdownTo) : null;
  const remaining = target != null ? target - Date.now() : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className={cn(
        "flex w-full min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-3 rounded-2xl border px-4 py-3 font-sans sm:px-5 sm:py-4",
        TONE_CLASSES[status.tone],
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xl",
            status.tone === "live" && "bg-emerald-500/20",
            status.tone === "warn" && "bg-amber-500/20",
            status.tone === "idle" && "bg-muted-foreground/10",
          )}
        >
          <CoolIcon name={status.glyph} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wider opacity-70">
            Trading Window
          </p>
          <p className="flex flex-wrap items-baseline gap-x-2 text-base font-semibold tracking-tight">
            <span className="truncate">{status.label}</span>
            {remaining != null && (
              <span className="font-mono tabular-nums">
                {formatDuration(remaining)}
              </span>
            )}
          </p>
        </div>
      </div>

      {win && win.status !== "weekend" && win.windowStart != null && win.windowEnd != null && (
        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-xs opacity-80">
          <span className="inline-flex items-baseline gap-1.5">
            <span className="opacity-60">Opens</span>
            <span className="font-mono">{formatClock(win.windowStart)}</span>
          </span>
          <span className="inline-flex items-baseline gap-1.5">
            <span className="opacity-60">Closes</span>
            <span className="font-mono">{formatClock(win.windowEnd)}</span>
          </span>
        </div>
      )}
    </motion.div>
  );
}

export type { WindowStatus };
